import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Distance } from '../types';

import { DISTANCES } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { ControlledAutoComplete, ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { schema } from '../schema';
import { omit, orderBy } from 'lodash';
import { countryCodeToOption } from '../utils';
import { useGetCountryCodeOptions } from 'hooks/useGetCountryCodeOptions';
import { useEffect } from 'react';

type FormProps = {
  handleClose: () => void;
  rowData?: Distance;
};

const CreateEditDistancesForm = ({ handleClose, rowData }: FormProps) => {
  const { t } = useTranslation();
  const codes = useGetCountryCodeOptions();

  const { mutateAsync: save, isLoading } = useSaveResource({
    url: DISTANCES,
    id: rowData?.distanceId,
    invalidateQueryKey: ['distances']
  });

  const methods = useForm({
    defaultValues: schema.cast(
      omit(rowData, ['fromCountryCode', 'toCountryCode'])
    ),
    resolver: yupResolver(schema),
    mode: 'onBlur'
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data) => {
    const payload = {
      ...data,
      fromCountryCode: data.fromCountryCode?.value ?? null,
      toCountryCode: data.toCountryCode?.value ?? null
    };

    save(payload).then(handleClose);
  };

  useEffect(() => {
    if (!rowData || !rowData.fromCountryCode || !rowData.toCountryCode) return;
    methods.reset({
      ...rowData,
      fromCountryCode: countryCodeToOption(rowData, codes, 'fromCountryCode'),
      toCountryCode: countryCodeToOption(rowData, codes, 'toCountryCode')
    } as any);
  }, [rowData, codes]);

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('distances.form.base')}
          validateFields={[
            'fromCountryCode',
            'fromPostalcode',
            'toCountryCode',
            'toPostalcode'
          ]}
        >
          <ControlledAutoComplete
            name="fromCountryCode"
            control={control}
            variant="outlined"
            label={t('distances.grid.fromCountryCode')}
            options={orderBy(codes, ['label'], ['asc'])}
            idProperty="value"
            optionLabelProp="label"
            disableSorting
            fullWidth
          />

          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="fromPostalcode"
              type="text"
              control={control}
              variant="outlined"
              label={t('distances.grid.fromPostalcode')}
            />
          </Box>

          <ControlledAutoComplete
            name="toCountryCode"
            control={control}
            variant="outlined"
            label={t('distances.grid.toCountryCode')}
            options={orderBy(codes, ['label'], ['asc'])}
            idProperty="value"
            optionLabelProp="label"
            disableSorting
            fullWidth
          />

          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="toPostalcode"
              type="text"
              control={control}
              variant="outlined"
              label={t('distances.grid.toPostalcode')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section
          title={t('distances.form.shortestRoute')}
          validateFields={['distanceShortest']}
        >
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="distanceShortest"
              type="number"
              control={control}
              variant="outlined"
              label={t('distances.grid.distanceShortest')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="traveltimeShortest"
              type="number"
              control={control}
              variant="outlined"
              label={t('distances.grid.traveltimeShortest')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section
          title={t('distances.form.fastestRoute')}
          validateFields={['distanceFastest']}
        >
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="distanceFastest"
              type="number"
              control={control}
              variant="outlined"
              label={t('distances.grid.distanceFastest')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="traveltimeFastest"
              type="number"
              control={control}
              variant="outlined"
              label={t('distances.grid.traveltimeFastest')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditDistancesForm;
